import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9 } from "styled-components";
import React from "react";
import { AnimatePresence, m } from "framer-motion";
import { up } from "~/features/app/screens";
import { type Mode, getThemeStyle } from "~/features/ui/get-theme-style";
import { type Shape } from "~/features/ui/slider";
import { AutoplayProgress } from "~/features/ui/slider/autoplay-progress";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { useGalleryBlock } from "~/features/blocks/inventory/gallery/context";
type Props = {
  children?: React.ReactNode;
  mode: Mode;
};
const controlStyle = {
  "marginTop": "1.6rem",
  "display": "inline-flex",
  "height": "100%",
  "width": "100%",
  "alignItems": "flex-start",
  "justifyContent": "center",
  "paddingLeft": "2.4rem",
  "paddingRight": "2.4rem",
  "paddingTop": "1.6rem",
  "paddingBottom": "1.6rem",
  "textAlign": "left",
  "@media (min-width: 1024px)": {
    "marginTop": "0",
    "justifyContent": "flex-start",
    "gap": "2.4rem"
  },
  ">*": {
    "transitionProperty": "opacity",
    "transitionDuration": "250ms",
    "transitionTimingFunction": "ease-in-out"
  },
  "@media (hover: hover)": {
    "&:hover>*": {
      "opacity": "1"
    }
  }
};
export type GalleryControlViewProps = {
  count: string;
  isLast?: boolean;
  isActive?: boolean;
  isFilled?: boolean;
  sliderInstance: React.RefObject<Shape | null>;
  onClick: () => void;
  mode: Mode;
  text?: React.ReactNode;
  title?: string | null;
};
export function GalleryControlView(props: GalleryControlViewProps) {
  const {
    count,
    isActive,
    sliderInstance,
    onClick,
    isFilled,
    mode,
    text,
    title
  } = props;
  const {
    layout
  } = useGalleryBlock();
  const themeStyle = getThemeStyle({
    mode: mode
  });
  const isLg = useMediaQuery(up("lg"));
  return <_StyledDiv role="listitem">
      <_StyledButton onClick={onClick} aria-label={`Go to slide ${title}`} $_css={[controlStyle, themeStyle.text, themeStyle.border, !isActive && {
      ">*": {
        "opacity": "0.4"
      }
    }, layout === "full-bleed" ? {
      "@media (min-width: 1024px)": {
        "paddingLeft": "var(--spacing-xl)",
        "paddingRight": "var(--spacing-xl)",
        "paddingTop": "var(--spacing-lg)",
        "paddingBottom": "var(--spacing-lg)"
      }
    } : {
      "@media (min-width: 1024px)": {
        "padding": "0",
        "paddingTop": "var(--spacing-lg)",
        "paddingBottom": "var(--spacing-lg)"
      }
    }]}>
        <_StyledSpan>
          {count}
        </_StyledSpan>
        <_StyledDiv2>
          <_StyledH $_css2={[{
          "whiteSpace": "nowrap",
          "fontSize": "1.6rem",
          "lineHeight": "1.5",
          "fontWeight": "400",
          "@media (min-width: 1024px)": {
            "whiteSpace": "normal"
          }
        }, layout === "full-bleed" ? {
          "@media (min-width: 1024px)": {
            "order": "2",
            "fontSize": "1.8rem",
            "lineHeight": "1.5",
            "letterSpacing": "0.018rem",
            "fontWeight": "400"
          }
        } : {
          "@media (min-width: 1024px)": {
            "fontSize": "2.4rem",
            "lineHeight": "1.3",
            "letterSpacing": "0.024rem",
            "fontWeight": "400"
          }
        }]}>
            {title}
          </_StyledH>
          {text && <_StyledMDiv initial={false} animate={isActive ? {
          height: "auto",
          opacity: 1
        } : {
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.4,
          ease: [0.25, 0.1, 0.25, 1]
        }}>
              <_StyledDiv3>{text}</_StyledDiv3>
            </_StyledMDiv>}
        </_StyledDiv2>
      </_StyledButton>
      <AnimatePresence>
        {isActive && <_StyledAutoplayProgress isFilled={isFilled} sliderInstance={sliderInstance} orientation={isLg && layout === "boxed" ? "horizontal" : "horizontal"} $_css3={[{
        "position": "absolute",
        "left": "0",
        "right": "0",
        "bottom": "0",
        "@media (min-width: 1024px)": {
          "bottom": "auto",
          "top": "0"
        }
      }]} />}
      </AnimatePresence>
    </_StyledDiv>;
}
export function GalleryControls(props: Props) {
  const {
    children,
    mode
  } = props;
  const {
    layout
  } = useGalleryBlock();
  const themeStyle = getThemeStyle({
    mode
  });
  return <_StyledDiv4 role="list" className="hide-scrollbar" $_css4={[{
    "display": "flex",
    "overflowX": "auto",
    "@media (min-width: 1024px)": {
      "padding": "var(--spacing-xl)",
      "paddingLeft": "var(--spacing-3xl)"
    }
  }, themeStyle.border, layout === "full-bleed" ? {
    "@media (min-width: 1024px)": {
      "overflow": "hidden",
      "borderBottomRightRadius": "4px",
      "borderBottomLeftRadius": "4px",
      "backgroundColor": "rgba(0, 7, 4, 0.1)",
      "--tw-backdrop-blur": "blur(8px)",
      "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)"
    }
  } : {
    "@media (min-width: 1024px)": {
      "flexDirection": "column"
    }
  }]}>
      {children}
    </_StyledDiv4>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-u2jk28-0"
})({
  "position": "relative",
  "width": "100%"
});
var _StyledButton = _styled2("button").withConfig({
  componentId: "sc-sf-u2jk28-1"
})`${p => p.$_css}`;
var _StyledSpan = _styled3("span").withConfig({
  componentId: "sc-sf-u2jk28-2"
})({
  "marginTop": "var(--spacing-sm)",
  "display": "none",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "display": "inline",
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-u2jk28-3"
})({
  "display": "flex",
  "flexDirection": "column"
});
var _StyledH = _styled5("h3").withConfig({
  componentId: "sc-sf-u2jk28-4"
})`${p => p.$_css2}`;
var _StyledMDiv = _styled6(m.div).withConfig({
  componentId: "sc-sf-u2jk28-5"
})({
  "display": "none !important",
  "overflow": "hidden",
  "@media (min-width: 1024px)": {
    "display": "block !important"
  }
});
var _StyledDiv3 = _styled7("div").withConfig({
  componentId: "sc-sf-u2jk28-6"
})({
  "paddingTop": "var(--spacing-lg)",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledAutoplayProgress = _styled8(AutoplayProgress).withConfig({
  componentId: "sc-sf-u2jk28-7"
})`${p => p.$_css3}`;
var _StyledDiv4 = /*#__PURE__*/_styled9("div").withConfig({
  displayName: "gallery-controls___StyledDiv4",
  componentId: "sc-1mxhuqg-0"
})(["", ""], p => p.$_css4);